import * as _ from 'lodash';

import { utils as commonUtils } from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import { ConcentrationLimitInsightsGqlDataGQLType } from 'types/dashboard-core-gqlTypes.generated';

import {
  CoreBorrowingBaseInsightsKeys,
  eligibilityKickoutsChartSharedKeys,
  excessConcentrationChartSharedKeys,
  ExpandedEligibilityAggregateInsightsData,
  PortfolioChartConfig,
  PortfolioChartFormat,
  PortfolioSingleChartConfig,
} from '../shared/portfolioChartUtils';
import { colors, transparency } from 'packages/common-frontend/colors';

// Each config should be in the order that the chart components are layered
// ie. the last item is the top-most layer on render

// if there are multiple axis (ie. multiple `format` types in a single config)
// the left axis should be the first item

const overallBorrowingBaseChartConfig: PortfolioChartConfig<CoreBorrowingBaseInsightsKeys> = {
  totalReceivableBorrowingBaseAmount: {
    chartType: 'bar',
    field: 'totalReceivableBorrowingBaseAmount',
    label: 'Borrowing Base (Receivables)',
    format: 'currency',
    color: `${colors.chart.sky}${transparency[50]}`,
    activeColor: colors.chart.sky,
    stackId: 'borrowingBase',
  },
  totalCashBorrowingBaseAmount: {
    chartType: 'bar',
    field: 'totalCashBorrowingBaseAmount',
    label: 'Borrowing Base (Cash)',
    format: 'currency',
    color: `${colors.chart.jasmine}${transparency[50]}`,
    activeColor: colors.chart.jasmine,
    stackId: 'borrowingBase',
  },
  currentPrincipalBalance: {
    chartType: 'dashedLine',
    field: 'currentPrincipalBalance',
    label: 'Principal Outstanding',
    format: 'currency',
    color: colors.black[95],
  },
};

const borrowingBaseReceivablesChartConfig: PortfolioChartConfig<CoreBorrowingBaseInsightsKeys> = {
  totalReceivableExposure: {
    chartType: 'area',
    field: 'totalReceivableExposure',
    label: 'Receivable Exposure',
    format: 'currency',
    color: `${colors.chart.lilac}${transparency[50]}`,
  },
  totalEligibleExposure: {
    chartType: 'area',
    field: 'totalEligibleExposure',
    label: 'Eligible Exposure',
    format: 'currency',
    color: `${colors.chart.mint}${transparency[50]}`,
  },
  totalEligibleExposureLessConcentrationKickouts: {
    chartType: 'area',
    field: 'totalEligibleExposureLessConcentrationKickouts',
    label: 'Eligible Exposure Less Concentration',
    format: 'currency',
    color: `${colors.chart.cream}${transparency[50]}`,
  },
  totalReceivableBorrowingBaseAmount: {
    chartType: 'area',
    field: 'totalReceivableBorrowingBaseAmount',
    label: 'Borrowing Base (Receivables)',
    format: 'currency',
    color: `${colors.chart.sky}${transparency[50]}`,
  },
  effectiveReceivableAdvanceRate: {
    chartType: 'dashedLine',
    field: 'effectiveReceivableAdvanceRate',
    label: 'Effective Receivable Advance Rate',
    format: 'percentage',
    color: colors.black[95],
  },
};

const borrowingBaseCashConfig: PortfolioChartConfig<CoreBorrowingBaseInsightsKeys> = {
  totalPreAdvanceCashContribution: {
    chartType: 'area',
    field: 'totalPreAdvanceCashContribution',
    label: 'Preadvance Cash',
    format: 'currency',
    color: `${colors.chart.teal}${transparency[50]}`,
  },
  totalCashBorrowingBaseAmount: {
    chartType: 'area',
    field: 'totalCashBorrowingBaseAmount',
    label: 'Borrowing Base (Cash Less Reserve)',
    format: 'currency',
    color: `${colors.chart.jasmine}${transparency[50]}`,
  },
};

const eligibleExposureChartConfig: PortfolioChartConfig<CoreBorrowingBaseInsightsKeys> = {
  totalReceivableExposure: {
    chartType: 'dashedLine',
    field: 'totalReceivableExposure',
    label: 'Total Receivable Exposure',
    format: 'currency',
    color: colors.black[95],
  },
  totalEligibleExposure: {
    chartType: 'bar',
    field: 'totalEligibleExposure',
    label: 'Total Eligible Exposure',
    format: 'currency',
    color: `${colors.chart.mint}${transparency[50]}`,
    activeColor: colors.chart.mint,
    stackId: 'exposure',
  },
};

const colorsForBars: string[] = [
  ..._.reverse([...Object.values(colors.brick)]),
  ...Object.values(colors.rose),
];

export const generateEligibilityKickoutsChartConfig = (
  chartData: ExpandedEligibilityAggregateInsightsData[],
): PortfolioChartConfig<string> => {
  const eligibilityColorsForBars: string[] = [...colorsForBars];

  const eligibilityPropertyChartConfigs: Partial<
    Record<
      keyof ExpandedEligibilityAggregateInsightsData,
      PortfolioSingleChartConfig
    >
  > = {};

  _.forEach(chartData, (datapoint) => {
    _.forEach(datapoint, (value, key) => {
      if (
        !_.includes(eligibilityKickoutsChartSharedKeys, key) &&
        !eligibilityPropertyChartConfigs[key] &&
        value !== 0
      ) {
        eligibilityPropertyChartConfigs[key] = {
          chartType: 'bar',
          field: key,
          label: commonUtils.toTitleCase(key),
          color: `${eligibilityColorsForBars[0] ?? colors.brick[300]}${
            transparency[50]
          }`,
          activeColor: eligibilityColorsForBars[0],
          format: 'currency',
          stackId: 'eligibilityKickoutProperty',
        };
        eligibilityColorsForBars.shift();
      }
    });
  });

  eligibilityPropertyChartConfigs.totalNetEligibilityKickout = {
    chartType: 'bar',
    field: 'totalNetEligibilityKickout',
    label: 'Total Eligibility Kickout',
    color: `${colors.chart.teal}${transparency[50]}`,
    activeColor: colors.chart.teal,
    format: 'currency',
  };

  return eligibilityPropertyChartConfigs;
};

type ChartGroupMap = {
  config: PortfolioChartConfig<any>;
  chartTitle: string;
};
type BorrowingBaseChartGroups =
  | 'borrowingBase'
  | 'eligibility'
  | 'concentration';

export const chartGroupMap: {
  [key in BorrowingBaseChartGroups]: ChartGroupMap[];
} = {
  borrowingBase: [
    {
      config: overallBorrowingBaseChartConfig,
      chartTitle: 'Overall Borrowing Base',
    },
    {
      config: borrowingBaseReceivablesChartConfig,
      chartTitle: 'Borrowing Base (Receivables)',
    },
    { config: borrowingBaseCashConfig, chartTitle: 'Borrowing Base (Cash)' },
  ],
  eligibility: [
    { config: eligibleExposureChartConfig, chartTitle: 'Eligible Exposure' },
  ],
  concentration: [],
};

export const getConcentrationLabelFormat = (
  config: CreditAgreement.DomainEntity.ConcentrationLimitProperty | undefined,
  respectConcentrationLimitFormat: boolean = true,
): PortfolioChartFormat => {
  // we used to format "concentration" value as "percentage".
  // Now with respectConcentrationLimitFormat flag turned on, we will correctly use the config for "concentration" value formatting.
  // we make this configurable, because in direct insert path, we have lot of concentration limits with invalid format set to limitType.
  // By default they will continue to work with "percentage" format.
  const valueType =
    config?._config_.concentrationLimitConfig?.valueConfig?.valueType;

  if (!respectConcentrationLimitFormat || !valueType) {
    return 'percentage';
  }

  return valueType === 'number' ? 'float' : valueType;
};

export const generateConcentrationLimitChartConfig = (
  format: PortfolioChartFormat,
  concentrationValueFormat: PortfolioChartFormat,
): PortfolioChartConfig<keyof ConcentrationLimitInsightsGqlDataGQLType> => {
  return {
    concentration: {
      chartType: 'line',
      field: 'concentration',
      label: 'Concentration',
      format,
      labelFormat: concentrationValueFormat,
      color: colors.chart.sky,
    },
    totalConcentrationLimitKickout: {
      chartType: 'bar',
      field: 'totalConcentrationLimitKickout',
      label: 'Total Excess Concentration',
      format: 'currency',
      color: `${colors.brick[500]}${transparency[50]}`,
      activeColor: colors.brick[500],
    },
    concentrationAmount: {
      chartType: 'none',
      field: 'concentrationAmount',
      label: 'Concentration Amount',
      format: 'currency',
      color: 'black',
    },
    concentrationLimit: {
      chartType: 'dashedLine',
      field: 'concentrationLimit',
      label: 'Concentration Limit',
      format,
      color: colors.black[95],
    },
  };
};

export const excessConcentrationAggregateChartConfig: PortfolioChartConfig<
  keyof ConcentrationLimitInsightsGqlDataGQLType
> = {
  totalConcentrationLimitKickout: {
    chartType: 'bar',
    field: 'totalConcentrationLimitKickout',
    label: 'Total Excess Concentration',
    format: 'currency',
    color: `${colors.chart.mint}${transparency[50]}`,
    activeColor: colors.chart.mint,
    stackId: 'exposure',
  },
};

export const generateExcessConcentrationChartConfig = (
  chartData: {
    [key: string]: Util.Primitive;
  }[],
  creditAgreementConcentrationLimits: CreditAgreement.DomainEntity.ConcentrationLimitProperty[],
): PortfolioChartConfig<string> => {
  const concentrationColorsForBars = [...colorsForBars];

  const concentrationPropertyChartConfigs: Partial<
    Record<
      keyof {
        [key: string]: Util.Primitive;
      },
      PortfolioSingleChartConfig
    >
  > = {};

  _.forEach(chartData, (datapoint) => {
    _.forEach(datapoint, (value, key) => {
      if (
        !_.includes(excessConcentrationChartSharedKeys, key) &&
        !concentrationPropertyChartConfigs[key] &&
        value !== 0
      ) {
        const creditAgreementConcentrationLimit = creditAgreementConcentrationLimits.find(
          (concentrationLimit) =>
            concentrationLimit._config_.concentrationLimitConfig?.propertyKey.toLowerCase() ===
            key.toLowerCase(),
        );

        const label =
          creditAgreementConcentrationLimit?.name ??
          commonUtils.toTitleCase(key);

        concentrationPropertyChartConfigs[key] = {
          chartType: 'bar',
          field: key,
          label,
          color: `${concentrationColorsForBars[0] ?? colors.brick[300]}${
            transparency[50]
          }`,
          activeColor: concentrationColorsForBars[0],
          format: 'currency',
          stackId: 'individualExcessConcentration',
        };
        concentrationColorsForBars.shift();
      }
    });
  });

  return concentrationPropertyChartConfigs;
};
